import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/admin/DateInput";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, BigButton } from "@/components/admin/common";

const EMPTY = { full_name: "", nationality: "", passport: "", dob: "", gender: "", cert_agency: "", cert_level: "", logged_dives: "",
  last_dive: "", emergency_name: "", emergency_phone: "", medical: "", dietary: "", cabin: "", phone: "", email: "" };
const TEXT = [["full_name", "Full name *", "sm:col-span-2"], ["nationality", "Nationality"], ["passport", "Passport / ID number"],
  ["dob", "Date of birth", "", "date"], ["cert_agency", "Certification agency (SSI, PADI…)"], ["cert_level", "Certification level"],
  ["logged_dives", "Logged dives", "", "number"], ["last_dive", "Last dive date", "", "date"], ["emergency_name", "Emergency contact name"],
  ["emergency_phone", "Emergency contact phone"], ["cabin", "Room / cabin"], ["phone", "Phone"], ["email", "Email", "", "email"]];

export default function GuestForm({ open, onClose, bookingId, guest, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(guest ? { ...EMPTY, ...guest, logged_dives: guest.logged_dives ?? "" } : EMPTY); }, [open, guest]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, booking_id: bookingId, logged_dives: f.logged_dives === "" ? null : Number(f.logged_dives) };
    try {
      if (guest) await api.put(`/admin/guests/${guest.id}`, body);
      else await api.post("/admin/guests", body);
      toast.success("Guest saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="font-admin max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="guest-form-dialog">
        <DialogHeader><DialogTitle>{guest ? "Edit guest" : "Add guest"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          {TEXT.map(([k, label, cls, type]) => (
            <Field key={k} label={label} className={cls}>
              {type === "date" ? <DateInput value={f[k]} onChange={set(k)} testid={`guest-${k}-input`} />
                : <Input type={type || "text"} required={k === "full_name"} value={f[k]} onChange={set(k)} className="h-12" data-testid={`guest-${k}-input`} />}
            </Field>
          ))}
          <Field label="Gender">
            <Select value={f.gender || "none"} onValueChange={(v) => set("gender")(v === "none" ? "" : v)}>
              <SelectTrigger className="h-12" data-testid="guest-gender-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Medical notes" className="sm:col-span-2"><Textarea value={f.medical} onChange={set("medical")} data-testid="guest-medical-input" /></Field>
          <Field label="Dietary needs" className="sm:col-span-2"><Input value={f.dietary} onChange={set("dietary")} className="h-12" data-testid="guest-dietary-input" /></Field>
          <BigButton type="submit" disabled={busy} className="sm:col-span-2" data-testid="guest-save-btn">{busy ? "Saving…" : "Save guest"}</BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}
